import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import expenseService from '../../services/ExpenseService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { filterShipmentLocations } from '../../utils/destinationType.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import { IndianRupee, Plus } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import ListPagination from '../common/ListPagination.js';
import { mobileCardClass, listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import ExpenseStatusModal from './ExpenseStatusModal.js';


const ExpenseList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAgent = user?.role === 'Agent';
  const canReview = ['System Admin', 'Admin', 'Accountant'].includes(user?.role);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [months, setMonths] = useState([]);
  const [locationHubs, setLocationHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(canReview ? 'pending' : '');
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [reviewExpense, setReviewExpense] = useState(null);
  const [statusViewExpense, setStatusViewExpense] = useState(null);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [locationHubFilter, setLocationHubFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  const statuses = isAgent
    ? ['pending', 'approved', 'rejected']
    : ['pending', 'approved', 'paid', 'rejected', 'cancelled'];

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (categoryFilter) filters.category = categoryFilter;
      if (monthFilter) filters.month = monthFilter;
      if (yearFilter) filters.year = yearFilter;
      if (statusFilter) filters.status = statusFilter;
      if (locationHubFilter) filters.location_hub_id = locationHubFilter;
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      const response = await expenseService.getAll(currentPage, perPage, filters);
      setExpenses((prev) => mergeLoadedPage(currentPage, response.data, prev));
      if (response.status_counts) setStatusCounts(response.status_counts);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load expenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, monthFilter, yearFilter, statusFilter, locationHubFilter, startDate, endDate, currentPage, perPage]);

  usePaginatedListFetch({
    fetchPage: fetchExpenses,
    setCurrentPage,
    currentPage,
    resetPageDeps: [categoryFilter, monthFilter, yearFilter, statusFilter, locationHubFilter, startDate, endDate],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [categoriesRes, monthsRes, hubsRes] = await Promise.all([
        expenseService.getCategories(),
        expenseService.getMonths(),
        locationHubService.getHubsForSelect({ brief: 1 }),
      ]);
      setCategories(categoriesRes.data || []);
      setMonths(monthsRes.data || []);
      setLocationHubs(filterShipmentLocations(hubsRes.data?.hubs || []));
    } catch (err) {
      console.error('Failed to load dropdown data', err);
    }
  };

  const handleDelete = async (expenseId, expenseName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete expense "${expenseName}"?`,
      'Delete Expense'
    );
    
    if (confirmed) {
      try {
        await expenseService.delete(expenseId);
        Alert.success('Expense deleted successfully!');
        
        if (expenses.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchExpenses();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete expense');
      }
    }
  };

  const toggleDropdown = (expenseId) => {
    setActiveDropdown(activeDropdown === expenseId ? null : expenseId);
  };

  const isSystemGeneratedExpense = (expense) =>
    ['salary', 'ml_commission', 'ml_commission_deduct'].includes(expense?.source_type);

  const canMutateExpense = (expense) => !isAgent && !isSystemGeneratedExpense(expense);

  const getExpenseExtraItems = (expense) => {
    const items = [];
    if (canMutateExpense(expense) && expense.status === 'pending' && canReview) {
      items.push({
        label: 'Open request',
        icon: '↗',
        onClick: () => openStatus(expense, true),
      });
    }
    items.push({
      label: 'View status',
      icon: 'ⓘ',
      onClick: () => openStatus(expense, false),
    });
    return items;
  };

  const openStatus = async (expense, asReview) => {
    try {
      const response = await expenseService.getById(expense.expense_id);
      const full = response.data || expense;
      if (asReview) setReviewExpense(full);
      else setStatusViewExpense(full);
    } catch (err) {
      if (asReview) setReviewExpense(expense);
      else setStatusViewExpense(expense);
    }
  };

  const submitReview = async ({ status, note }) => {
    if (!reviewExpense) return;
    try {
      setReviewSaving(true);
      await expenseService.review(reviewExpense.expense_id, { status, note });
      Alert.success(status === 'approved' ? 'Expense approved' : 'Expense rejected');
      setReviewExpense(null);
      fetchExpenses();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update expense status');
    } finally {
      setReviewSaving(false);
    }
  };

  const clearFilters = () => {
    setCategoryFilter('');
    setMonthFilter('');
    setYearFilter('');
    setStatusFilter('');
    setLocationHubFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters = categoryFilter || monthFilter || yearFilter || statusFilter || locationHubFilter || startDate || endDate;

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <DashboardLayout>
              <div className="w-full min-w-0 max-w-full mx-auto">
                <ListPageHeader
                  title="Expenses"
                  icon={IndianRupee}
                  titleClassName="text-secondary"
                  iconClassName="w-5 h-5 text-buttonBg shrink-0"
                  className="mb-4 sm:mb-4"
                >
                  <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/expenses/add')}>
                    Add Expense
                  </Button>
                </ListPageHeader>

                {canReview && (
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
                    {[
                      ['pending', 'Pending', 'border-amber-200 bg-amber-50 text-amber-900'],
                      ['approved', 'Approved', 'border-blue-200 bg-blue-50 text-blue-900'],
                      ['rejected', 'Rejected', 'border-red-200 bg-red-50 text-red-900'],
                    ].map(([key, label, tone]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setStatusFilter(key)}
                        className={`rounded-xl border px-2 py-2 sm:px-3 sm:py-3 text-left ${tone} ${statusFilter === key ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                      >
                        <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide">{label}</p>
                        <p className="text-lg sm:text-2xl font-semibold tabular-nums">{statusCounts[key] || 0}</p>
                      </button>
                    ))}
                  </div>
                )}

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Category</label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Categories</option>
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Month</label>
                    <select
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Months</option>
                      {months.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Year</label>
                    <select
                      value={yearFilter}
                      onChange={(e) => setYearFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Years</option>
                      {years.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Status</option>
                      {statuses.map((status) => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Location Hub</label>
                    <select
                      value={locationHubFilter}
                      onChange={(e) => setLocationHubFilter(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
                    >
                      <option value="">All Locations</option>
                      {sortLocationsAsc(locationHubs).map((hub) => (
                        <option key={hub.id} value={hub.id}>
                          {formatLocationDropdownLabel(hub)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                    />
                  </div>
                </ListFilterPanel>

                {loading && currentPage === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader fullScreen={false} size="default" message="Loading expenses..." />
                  </div>
                ) : expenses.length === 0 ? (
                  <div className="bg-white p-8 rounded-xl shadow-sm text-center text-gray-600">No expenses found</div>
                ) : (
                  <>
                    {/* Table - Desktop View */}
                    <div className="hidden xl:block bg-white rounded-xl shadow-sm w-full min-w-0">
                      <div className="w-full overflow-x-auto overscroll-x-contain">
                        <table className="w-full min-w-[1100px] border-collapse divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Expense ID</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Category</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Shipment</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">Location Hub</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Date</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Amount</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[120px]">Vendor</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[140px]">{t('common.createdBy')}</th>
                              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                            </tr>
                          </thead>

                          <tbody className="bg-white divide-y divide-gray-200">
                            {expenses.map((expense) => (
                              <tr key={expense.expense_id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 whitespace-nowrap">
                                    <button
                                      onClick={() => navigate(`/expenses/view/${expense.expense_id}`)}
                                      className={listIdLinkClass}
                                    >
                                      {expense.expense_id}
                                    </button>
                                  </td>

                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{expense.category_display}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {expense.shipment_id ? (
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/shipments/view/${expense.shipment_id}`)}
                                      className={listIdLinkClass}
                                    >
                                      {expense.shipment_id}
                                    </button>
                                  ) : '—'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 min-w-[140px]">
                                  <span className="block truncate max-w-[180px]" title={expense.location_hub_name || ''}>
                                    {expense.location_hub_name || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{formatDate(expense.expense_date)}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{formatAmount(expense.amount)}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 min-w-[120px]">
                                  <span className="block truncate max-w-[160px]">{expense.vendor_name || 'N/A'}</span>
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span className={`px-3 py-1 text-sm text-gray-600`}>
                                    {expense.status_display}
                                  </span>
                                </td>

                                <td className="px-4 py-3 min-w-[140px]">
                                  <div className="max-w-[180px]">
                                    <p
                                      className="truncate text-sm font-medium text-gray-800"
                                      title={expense.created_by_name || expense.created_by || ''}
                                    >
                                      {expense.created_by_name || expense.created_by || 'N/A'}
                                    </p>
                                    {(expense.location_hub_name || expense.created_by_location_hub) && (
                                      <p
                                        className="truncate text-xs text-gray-500 mt-0.5"
                                        title={expense.location_hub_name || expense.created_by_location_hub}
                                      >
                                        {expense.location_hub_name || expense.created_by_location_hub}
                                      </p>
                                    )}
                                  </div>
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap relative">
                                    <RowActionsMenu
                                      isOpen={activeDropdown === expense.expense_id}
                                      onToggle={() => toggleDropdown(expense.expense_id)}
                                      onClose={() => setActiveDropdown(null)}
                                      menuRef={dropdownRef}
                                      onView={() => navigate(`/expenses/view/${expense.expense_id}`)}
                                      onEdit={canMutateExpense(expense) ? () => navigate(`/expenses/edit/${expense.expense_id}`) : undefined}
                                      onDelete={canMutateExpense(expense) ? () => handleDelete(expense.expense_id, expense.category_display) : undefined}
                                      extraItems={getExpenseExtraItems(expense)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <ListPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          loading={loading}
                          onNext={() => setCurrentPage((page) => page + 1)}
                        />
                    </div>

                    {/* Card View - Mobile/Tablet */}
                    <div className="xl:hidden space-y-3 sm:space-y-4">
                      {expenses.map((expense) => (
                        <div key={expense.expense_id} className={`${mobileCardClass} sm:p-5`}>
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="flex-1 min-w-0">
                              <h3
                                className={listIdLinkMobileClass}
                                onClick={() => navigate(`/expenses/view/${expense.expense_id}`)}
                              >
                                {expense.expense_id}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-600">{expense.category_display}</p>
                              {expense.shipment_id && (
                                <p className="text-xs text-gray-500 mt-0.5">Shipment {expense.shipment_id}</p>
                              )}
                              <p className="text-lg sm:text-xl font-bold text-gray-900 mt-1 md:hidden">{formatAmount(expense.amount)}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusBadgeClass(expense.status)}`}>
                                {expense.status_display}
                              </span>
                              <RowActionsMenu
                                isOpen={activeDropdown === expense.expense_id}
                                onToggle={() => toggleDropdown(expense.expense_id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/expenses/view/${expense.expense_id}`)}
                                onEdit={canMutateExpense(expense) ? () => navigate(`/expenses/edit/${expense.expense_id}`) : undefined}
                                onDelete={canMutateExpense(expense) ? () => handleDelete(expense.expense_id, expense.category_display) : undefined}
                                extraItems={getExpenseExtraItems(expense)}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                            <div className="hidden md:block">
                              <span className="text-gray-500">Amount:</span>
                              <p className="text-gray-900 font-medium">{formatAmount(expense.amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <p className="text-gray-900 font-medium">{formatDate(expense.expense_date)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Location Hub:</span>
                              <p className="text-gray-900 font-medium">{expense.location_hub_name || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Vendor:</span>
                              <p className="text-gray-900 font-medium">{expense.vendor_name || 'N/A'}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Month/Year:</span>
                              <p className="text-gray-900 font-medium">{expense.month_year}</p>
                            </div>
                          </div>
                        </div>
                      ))}

                      
                    </div>
                  </>
                )}
              </div>
      <ExpenseStatusModal
        open={Boolean(reviewExpense)}
        title="Open request"
        expense={reviewExpense}
        mode="review"
        saving={reviewSaving}
        onClose={() => setReviewExpense(null)}
        onSubmit={submitReview}
      />
      <ExpenseStatusModal
        open={Boolean(statusViewExpense)}
        title="View status"
        expense={statusViewExpense}
        mode="view"
        onClose={() => setStatusViewExpense(null)}
      />
    </DashboardLayout>
  );
};

export default ExpenseList;